/**
 * Route Access Utility
 *
 * Centralized route permission logic for the entire application.
 * This file is the single source of truth for determining:
 * - Which routes require internal access
 * - Which routes are always accessible (free routes)
 * - Whether a user can access a specific route
 * - Whether UI elements (cards, sidebar items) should be disabled
 *
 * Route Access Rules:
 * 1. INTERNAL routes: Only accessible to internal users (isInternal = true)
 * 2. ALWAYS_ACCESSIBLE routes: Accessible to ALL authenticated users regardless of license
 * 3. LICENSED routes: Require Studio entitlement (`hasStudioEntitlement`: a valid license or a paid credits plan)
 *
 * User Access Levels:
 * - Internal users: Can access everything
 * - Licensed external users: Can access everything except internal routes
 * - Unlicensed external users: Can only access ALWAYS_ACCESSIBLE routes
 */

import {
    hasStudioEntitlement,
    type StudioEntitlementOrg,
} from '@/lib/studio-entitlement/studio-entitlement';
import { legacyDashboardPath } from '@/@dashboards/hubs';
import { StudioRoutes } from '@/app/routes';
import { STUDIO_NAV_FEATURE_FLAGS } from '@/@studio/types/constants';

// ============================================================================
// Route Definitions
// ============================================================================

/**
 * Routes that require internal user access.
 * Non-internal users should be redirected to /home when trying to access these.
 */
// `/vtrackers` is the internal-only vTrackers dashboard hub (`@dashboards/hubs.ts`).
export const INTERNAL_ROUTES = ['/tools', '/feasibility', '/ai', '/oe-coding', '/vtrackers'] as const;

/**
 * Routes that are ALWAYS accessible to authenticated users, regardless of license status.
 * These routes should never be disabled and never redirect to billing.
 *
 * Note: /dani is NOT here — it requires hasStudioAIAccess or isUnaffiliated (freemium).
 */
export const ALWAYS_ACCESSIBLE_ROUTES = [
    StudioRoutes.HOME, // /home
    '/account', // All account routes
    '/sign-out', // Sign out route
    StudioRoutes.INSIGHTS_CONTINUE, // Public CTA trial chat (also works while logged in)
    '/connect', // Connect channel detail pages — same visibility as the Home tiles that link to them
] as const;

/**
 * AI Crosstabs lives under `/ai` but is not a DANI feature, and not internal. It is part of Studio,
 * so it opens on the one Studio rule: internal staff, or `hasStudioEntitlement` (a valid license or
 * a paid credits plan). DANI access alone does not open it (M4S-5777 X1, decision A1); the Ask DANI
 * panel inside it still needs DANI. Until the classic tool is retired for clients, the pages
 * themselves also wait for `crosstabsMode` (`src/app/(private)/ai/crosstabs/layout.tsx`).
 * Spec: docs/pdlc/crosstabs-retire-legacy.md §3, docs/pdlc/crosstabs-access-lifecycle.md §1.
 */
export const AI_CROSSTABS_ROUTES = ['/ai/crosstabs'] as const;

/**
 * Routes that require a specific feature flag, on top of whatever
 * license/internal/Studio-AI rule already applies — an external user must
 * pass both. Migrated from the pre-M4S-5697 CMS-card permission check
 * (`useRoutePermissions`, which read the legacy home page's `STUDIO_HOME_PAGE`
 * cards and their `featureFlag`); that page is gone, but these seven routes
 * were the only cards in it whose `featureFlag` was ever actually reachable —
 * every other card either had none, or sat under `/dani`/`/ai` where the
 * Studio-AI-route check already short-circuits the CMS check before it runs.
 * None of those seven cards carried an `Internal`/`Clients`/`Users`
 * restriction of their own, so this table is a complete, faithful
 * replacement — not a simplification.
 */
const FEATURE_FLAGGED_ROUTES: ReadonlyArray<{ prefix: string; flag: string }> = [
    { prefix: '/discover/insights', flag: STUDIO_NAV_FEATURE_FLAGS.SPOTLIGHT },
    { prefix: '/reports', flag: STUDIO_NAV_FEATURE_FLAGS.REPORTS },
    { prefix: '/slide-creation', flag: STUDIO_NAV_FEATURE_FLAGS.SLIDE_CREATION },
    { prefix: '/ai/studio-catalog', flag: STUDIO_NAV_FEATURE_FLAGS.STUDIO_CATALOG },
    { prefix: '/current-market-trends', flag: STUDIO_NAV_FEATURE_FLAGS.DATA_PRODUCTS },
    { prefix: '/brand-image', flag: STUDIO_NAV_FEATURE_FLAGS.DATA_PRODUCTS },
    { prefix: '/custom-dashboards/abc_norms', flag: STUDIO_NAV_FEATURE_FLAGS.ABC_DTS_NORMS },
];

/** The feature flag a path requires, or null if none of `FEATURE_FLAGGED_ROUTES` matches. */
function getRequiredFeatureFlag(path: string): string | null {
    // A hub dashboard (`/norm-databases/abc_norms`) needs the flag of the path it
    // lived at before the hubs (`/custom-dashboards/abc_norms`).
    const flagPath = legacyDashboardPath(path) ?? path;
    const match = FEATURE_FLAGGED_ROUTES.find((entry) =>
        matchesRoutePrefix(flagPath, [entry.prefix]),
    );
    return match?.flag ?? null;
}

// ============================================================================
// Freemium Account Constants
// ============================================================================

/**
 * Routes accessible to freemium account users.
 * Freemium users can ONLY access these routes — all others redirect to /dani.
 */
export const FREEMIUM_ACCESSIBLE_ROUTES = [
    StudioRoutes.STUDIO_AI, // /dani
    StudioRoutes.INSIGHTS_CONTINUE, // CTA trial surface — do not redirect to /dani
    '/sign-out', // Allow signing out
    '/account', // Account settings
] as const;

// ============================================================================
// Type Definitions
// ============================================================================

export interface RouteAccessUser {
    isInternal?: boolean;
    hasStudioAIAccess?: boolean;
    isUnaffiliated?: boolean;
}

/** The org fields the rules read. Callers pass the org itself, so a field the entitlement rule adds reaches it. */
export interface RouteAccessOrg extends StudioEntitlementOrg {
    hasClientStudioAIAccess?: boolean;
}

export interface RouteAccessParams {
    user?: RouteAccessUser | null;
    org?: RouteAccessOrg | null;
    /** Checks a resolved flag's value. Omit (or pass a still-resolving
     * checker) while flags are loading — `getRouteAccess` only ever uses
     * this to veto an otherwise-granted route, never to grant one, so an
     * absent checker just means `FEATURE_FLAGGED_ROUTES` is not enforced
     * for that call (matches the pre-removal behavior of allowing access
     * while the old CMS flags were still being fetched). */
    hasFlag?: (flag: string) => boolean;
}

export interface RouteAccessResult {
    /** Whether the user can access this route */
    canAccess: boolean;
    /** Whether the route should be disabled (shown but not clickable) */
    isDisabled: boolean;
    /** Where to redirect if access is denied */
    redirectTo?: string;
    /** Reason for access denial */
    reason?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Checks if a path matches any of the route prefixes
 */
function matchesRoutePrefix(path: string, routes: readonly string[]): boolean {
    const normalizedPath = path.replace(/\/$/, ''); // Remove trailing slash
    return routes.some((route) => {
        const normalizedRoute = route.replace(/\/$/, '');
        return (
            normalizedPath === normalizedRoute || normalizedPath.startsWith(`${normalizedRoute}/`)
        );
    });
}

/**
 * Checks if a route is an internal-only route
 */
export function isInternalRoute(path: string): boolean {
    return !isAiCrosstabsRoute(path) && matchesRoutePrefix(path, INTERNAL_ROUTES);
}

/**
 * Checks if a route is AI Crosstabs — see `AI_CROSSTABS_ROUTES`. It is neither an internal route
 * nor a Studio AI route, although both of their prefixes cover it.
 */
export function isAiCrosstabsRoute(path: string): boolean {
    return matchesRoutePrefix(path, AI_CROSSTABS_ROUTES);
}

/**
 * Checks if a route is always accessible (free route)
 */
export function isAlwaysAccessibleRoute(path: string): boolean {
    return matchesRoutePrefix(path, ALWAYS_ACCESSIBLE_ROUTES);
}

/**
 * Checks if a route is accessible to freemium account users
 */
export function isFreemiumAccessibleRoute(path: string): boolean {
    return matchesRoutePrefix(path, FREEMIUM_ACCESSIBLE_ROUTES);
}

/**
 * Determines if a user is unaffiliated (belongs to the temporary freemium org).
 * These users are restricted to the DANI chat experience only — sidebar hidden,
 * history hidden, always redirected to /dani.
 *
 * @param params - User and org information
 * @returns true if the user is unaffiliated
 */
export function isFreemiumUser(params: RouteAccessParams): boolean {
    return params.user?.isUnaffiliated === true;
}

/**
 * Checks if a route is a Studio AI (DANI) route.
 * These routes require hasStudioAIAccess or isUnaffiliated (freemium).
 *
 * Includes the /ai sandbox namespace (routes authored in the /dani shell and
 * synced into production), which is gated the same way as /dani.
 */
export function isStudioAIRoute(path: string): boolean {
    return (
        !isAiCrosstabsRoute(path) &&
        matchesRoutePrefix(path, [StudioRoutes.STUDIO_AI, StudioRoutes.STUDIO_AI_SANDBOX])
    );
}

/**
 * Checks if a route requires a valid license
 * (any route that is NOT internal and NOT always accessible)
 */
export function requiresLicense(path: string): boolean {
    return !isInternalRoute(path) && !isAlwaysAccessibleRoute(path);
}

// ============================================================================
// Main Access Functions
// ============================================================================

/**
 * Determines full access information for a route.
 * This is the main function for checking route access.
 *
 * @param path - The route path to check
 * @param params - User and org information
 * @returns Complete access information including canAccess, isDisabled, and redirectTo
 */
export function getRouteAccess(path: string, params: RouteAccessParams): RouteAccessResult {
    const result = getBaseRouteAccess(path, params);

    // A required feature flag only ever narrows an otherwise-granted route —
    // never widens one the checks above already denied — and never applies
    // to internal users, matching `FEATURE_FLAGGED_ROUTES`'s own contract.
    if (result.canAccess && !(params.user?.isInternal ?? false)) {
        const requiredFlag = getRequiredFeatureFlag(path);
        if (requiredFlag && params.hasFlag && !params.hasFlag(requiredFlag)) {
            return {
                canAccess: false,
                isDisabled: true,
                redirectTo: StudioRoutes.HOME,
                reason: 'You do not have access to this feature',
            };
        }
    }

    return result;
}

function getBaseRouteAccess(path: string, params: RouteAccessParams): RouteAccessResult {
    const { user, org } = params;
    const isInternal = user?.isInternal || false;
    const hasEntitlement = hasStudioEntitlement(org);
    const hasStudioAIAccess = user?.hasStudioAIAccess || false;
    const hasClientStudioAIAccess = org?.hasClientStudioAIAccess || false;

    // Unaffiliated (freemium) users: restricted to DANI and sign-out only
    if (isFreemiumUser(params)) {
        if (isFreemiumAccessibleRoute(path)) {
            return { canAccess: true, isDisabled: false };
        }
        return {
            canAccess: false,
            isDisabled: true,
            redirectTo: StudioRoutes.STUDIO_AI,
            reason: 'Freemium users can only access DANI',
        };
    }

    // AI Crosstabs: internal staff or the Studio rule, nothing else (`AI_CROSSTABS_ROUTES`).
    if (isAiCrosstabsRoute(path)) {
        if (isInternal || hasEntitlement) {
            return { canAccess: true, isDisabled: false };
        }
        return {
            canAccess: false,
            isDisabled: true,
            redirectTo: StudioRoutes.HOME,
            reason: 'A valid license is required to access this feature',
        };
    }

    // Studio AI (DANI) routes require per-user access, org-wide trial access, or internal.
    if (isStudioAIRoute(path)) {
        if (hasStudioAIAccess || hasClientStudioAIAccess) {
            return { canAccess: true, isDisabled: false };
        }
        return {
            canAccess: false,
            isDisabled: true,
            redirectTo: StudioRoutes.HOME,
            reason: 'Studio AI access is required',
        };
    }

    // Internal routes: only accessible to internal users
    if (isInternalRoute(path)) {
        if (!isInternal) {
            return {
                canAccess: false,
                isDisabled: true,
                redirectTo: StudioRoutes.HOME,
                reason: 'This route requires internal access',
            };
        }
        // Internal users can access internal routes
        return { canAccess: true, isDisabled: false };
    }

    // Always accessible routes: never disabled, always accessible
    if (isAlwaysAccessibleRoute(path)) {
        return { canAccess: true, isDisabled: false };
    }

    // Licensed routes: require valid license or internal access
    // Internal users can access everything
    if (isInternal) {
        return { canAccess: true, isDisabled: false };
    }

    // External users with valid license can access
    if (hasEntitlement) {
        return { canAccess: true, isDisabled: false };
    }

    // External users with Studio AI access can still access some features
    // but licensed routes should be disabled
    if (hasStudioAIAccess) {
        return {
            canAccess: false,
            isDisabled: true,
            redirectTo: StudioRoutes.HOME,
            reason: 'A valid license is required to access this feature',
        };
    }

    // No license, no Studio AI access - redirect to billing
    return {
        canAccess: false,
        isDisabled: true,
        redirectTo: StudioRoutes.HOME,
        reason: 'A valid license is required to access this feature',
    };
}

/**
 * Simple check if a user can access a route.
 * Use this when you only need a boolean result.
 *
 * @param path - The route path to check
 * @param params - User and org information
 * @returns true if the user can access the route
 */
export function canAccessRoute(path: string, params: RouteAccessParams): boolean {
    return getRouteAccess(path, params).canAccess;
}

/**
 * Check if a card/link should be disabled based on user access.
 * Use this for disabling UI elements like cards and sidebar items.
 *
 * @param path - The route path of the card/link
 * @param params - User and org information
 * @returns true if the element should be disabled
 */
export function shouldDisableRoute(path: string, params: RouteAccessParams): boolean {
    if (!path) return false;
    return getRouteAccess(path, params).isDisabled;
}

/**
 * Check if the current route requires a redirect.
 * Use this in useEffect hooks to redirect users from restricted routes.
 *
 * @param currentPath - The current route path
 * @param params - User and org information
 * @returns The redirect path if needed, or null if no redirect required
 */
export function getRequiredRedirect(currentPath: string, params: RouteAccessParams): string | null {
    const access = getRouteAccess(currentPath, params);
    if (!access.canAccess && access.redirectTo) {
        return access.redirectTo;
    }
    return null;
}

/**
 * Default landing route after authentication. Always /home (PRD
 * STUDIO-HOME-001 H-A89a) — Ask DANI is one destination among several on
 * that screen, not the landing page, regardless of DANI access.
 */
export function getPostAuthLandingUrl(): string {
    return StudioRoutes.HOME;
}

/**
 * Check if a user should be redirected based on license status.
 * This specifically handles the "expired license" case - only redirect
 * if the user is on a route that requires a license.
 *
 * @param currentPath - The current route path
 * @param params - User and org information
 * @returns true if user should be redirected due to expired/missing license
 */
export function shouldRedirectForLicense(currentPath: string, params: RouteAccessParams): boolean {
    const { user, org } = params;

    // Internal users are never redirected for license
    if (user?.isInternal) return false;

    // Freemium account users are never redirected for license
    // (they are restricted to /dani by the freemium route access rules)
    if (isFreemiumUser(params)) return false;

    // Entitled orgs (a valid license or a paid credits plan) are never redirected
    if (hasStudioEntitlement(org)) return false;

    // Users on always-accessible routes are never redirected
    if (isAlwaysAccessibleRoute(currentPath)) return false;

    // Users on Studio AI routes with per-user or org-trial access are never redirected for license.
    // AI Crosstabs is not one of them: DANI access does not open it (`AI_CROSSTABS_ROUTES`).
    if (isStudioAIRoute(currentPath) && (user?.hasStudioAIAccess || org?.hasClientStudioAIAccess)) {
        return false;
    }

    // Users on internal routes are handled separately (by internal check)
    if (isInternalRoute(currentPath)) return false;

    // User is on a licensed route without a valid license
    return true;
}
